/**
 * Schema Request Handler
 * Handles SCHEMA_REQ messages to return the database schema
 */

import type { Message } from '../types';
import { schema } from '../userResponse/schema';
import { logger } from '../utils/logger';

interface SchemaRequestPayload {
  /** If true, returns the formatted documentation string instead of raw JSON */
  formatted?: boolean;
}

interface SchemaResponsePayload {
  success: boolean;
  error?: string;
  data?: {
    schema: any;
    formatted?: string;
  };
}

/**
 * Handle schema request
 * Returns the database schema from schema.json
 */
export async function handleSchemaRequest(
  message: any,
  sendMessage: (msg: Message) => void
): Promise<void> {
  const startTime = Date.now();

  try {
    const payload = message.payload as SchemaRequestPayload;
    const formatted = payload?.formatted ?? false;

    logger.info(`[SchemaRequest] Processing schema request (formatted: ${formatted})`);

    // Get the schema
    const schemaData = schema.getSchema();

    if (!schemaData) {
      const response: Message = {
        id: message.id,
        type: 'SCHEMA_RES',
        from: { type: 'data-agent' },
        to: message.from,
        payload: {
          success: false,
          error: 'Schema not found or failed to load',
        } as SchemaResponsePayload,
      };
      sendMessage(response);
      return;
    }

    // Build response data
    const responseData: SchemaResponsePayload['data'] = {
      schema: schemaData,
    };

    // Include formatted documentation if requested
    if (formatted) {
      responseData.formatted = schema.generateSchemaDocumentation();
    }

    const executionMs = Date.now() - startTime;
    logger.info(`[SchemaRequest] Schema retrieved successfully in ${executionMs}ms`);

    const response: Message = {
      id: message.id,
      type: 'SCHEMA_RES',
      from: { type: 'data-agent' },
      to: message.from,
      payload: {
        success: true,
        data: responseData,
      } as SchemaResponsePayload,
    };

    sendMessage(response);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error(`[SchemaRequest] Error: ${errorMsg}`);

    const response: Message = {
      id: message.id,
      type: 'SCHEMA_RES',
      from: { type: 'data-agent' },
      to: message.from,
      payload: {
        success: false,
        error: errorMsg,
      } as SchemaResponsePayload,
    };

    sendMessage(response);
  }
}
